import { createBrowserClient } from "@supabase/ssr";
import { createClient } from "@supabase/supabase-js";
import { Capacitor } from "@capacitor/core";
import type { Database } from "@/lib/types/database.types";

/**
 * Capacitor's iOS WebView serves the app from `capacitor://localhost` and
 * the Android WebView from `https://localhost`. On those origins the cookies
 * `@supabase/ssr`'s `createBrowserClient` writes don't survive a
 * window.location.href navigation — so after signInWithIdToken succeeds, the
 * very next page (`/map`) calls `getUser()` against an empty cookie jar and
 * the auth guard bounces the user back to `/login`. Using `@supabase/supabase-js`
 * directly with localStorage as the session store fixes it for the native
 * shell while leaving the web build on cookies so `/auth/callback` still
 * works for the OAuth redirect flow.
 */
// Memoized singleton (browser only). Every data-layer call used to build a
// fresh client, which on native re-runs createClient + re-reads localStorage +
// re-inits the GoTrue auth lock on each query — wasteful when ~38 call sites
// hammer it. Cache one instance per browser session; the SSR path stays
// per-call (no window) so server renders never share a client.
let cachedClient: ReturnType<typeof createClient<Database>> | ReturnType<typeof createBrowserClient<Database>> | null = null;

export function createSupabaseBrowserClient() {
  if (cachedClient) return cachedClient;

  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    throw new Error(
      "Missing Supabase env vars: NEXT_PUBLIC_SUPABASE_URL / NEXT_PUBLIC_SUPABASE_ANON_KEY",
    );
  }

  if (typeof window !== "undefined" && Capacitor.isNativePlatform()) {
    cachedClient = createClient<Database>(url, anonKey, {
      auth: {
        storage: window.localStorage,
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
    });
    return cachedClient;
  }

  const client = createBrowserClient<Database>(url, anonKey);
  // Only memoize in the browser — never share a client across SSR requests.
  if (typeof window !== "undefined") cachedClient = client;
  return client;
}
